import { ApiError } from './api';
import { isAuthenticated } from './auth';
import { getShopCart, getShopProduct, updateShopCart } from '../api/client';

// Anonymous "device cart": the slugs + quantities a signed-out visitor collects,
// kept in localStorage so they survive reloads, then merged into the signed-in
// customer's server cart on sign-in. It holds no PII (only product slugs +
// quantities), so client-side storage is safe (RAD OI-011). The authed cart is
// server-authoritative in `shop_carts`; this is only the pre-auth holding area.
const DEVICE_CART_KEY = 'asmaluz.cart.v1';
// Mirrors the server's per-line cap (internal/shop/http.go maxQuantity).
const MAX_QTY = 99;

export interface DeviceLine {
  slug: string;
  quantity: number;
}

function clampQty(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  return n > MAX_QTY ? MAX_QTY : Math.trunc(n);
}

function readDeviceCart(): DeviceLine[] {
  try {
    const raw = localStorage.getItem(DEVICE_CART_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (l): l is DeviceLine =>
          !!l &&
          typeof l === 'object' &&
          typeof (l as DeviceLine).slug === 'string' &&
          Number.isFinite((l as DeviceLine).quantity),
      )
      .map((l) => ({ slug: l.slug, quantity: clampQty(l.quantity) }))
      .filter((l) => l.quantity > 0);
  } catch {
    // Corrupt or unavailable storage → treat as an empty cart, never throw.
    return [];
  }
}

function writeDeviceCart(lines: DeviceLine[]): void {
  try {
    localStorage.setItem(DEVICE_CART_KEY, JSON.stringify(lines));
  } catch {
    // Storage full/disabled — best-effort; the cart just won't persist.
  }
}

function clearDeviceCart(): void {
  try {
    localStorage.removeItem(DEVICE_CART_KEY);
  } catch {
    // best-effort
  }
}

// deviceCartCount is the total item count held in the anonymous device cart.
export function deviceCartCount(): number {
  return readDeviceCart().reduce((n, l) => n + l.quantity, 0);
}

// cartCount returns the current total item count for the signed-in server cart
// or the anonymous device cart. Used by the nav cart badge.
export async function cartCount(): Promise<number> {
  if (!isAuthenticated()) {
    return deviceCartCount();
  }
  try {
    const cart = await getShopCart();
    return sumCartLines(cart.lines);
  } catch {
    return 0;
  }
}

// CartViewLine is one rendered cart line, carrying the name + price a drawer or
// the checkout summary needs (the anonymous device cart stores only slug+qty, so
// those are hydrated from the catalog here).
export interface CartViewLine {
  slug: string;
  name: string;
  quantity: number;
  priceCents: number;
  currency: string;
}

export interface CartView {
  lines: CartViewLine[];
  subtotalCents: number;
  currency: string;
}

// loadCart returns a consistent, rendered view of the cart for both audiences:
// the signed-in server cart (which already carries name/price snapshots) or the
// anonymous device cart (slug+qty only, hydrated from the catalog; a line whose
// product is gone is dropped). The drawer and the checkout summary both read this.
export async function loadCart(): Promise<CartView> {
  if (isAuthenticated()) {
    const cart = await getShopCart();
    const lines: CartViewLine[] = (cart.lines ?? []).map((l) => ({
      slug: l.slug,
      name: l.name,
      quantity: l.quantity,
      priceCents: l.priceCents,
      currency: l.currency,
    }));
    return { lines, subtotalCents: cart.subtotalCents ?? 0, currency: cart.currency ?? '' };
  }

  const device = readDeviceCart();
  const lines: CartViewLine[] = [];
  let subtotal = 0;
  let currency = '';
  for (const d of device) {
    try {
      const p = await getShopProduct(d.slug);
      lines.push({ slug: d.slug, name: p.name, quantity: d.quantity, priceCents: p.priceCents, currency: p.currency });
      subtotal += p.priceCents * d.quantity;
      if (!currency) currency = p.currency;
    } catch {
      // Product gone/unpublished — drop it from the view.
    }
  }
  return { lines, subtotalCents: subtotal, currency };
}

// CheckoutCart is a cart view priced for checkout review: every line carries its
// LIVE product price (not the cart snapshot), so the total shown at review equals
// the total the server will charge — placeOrder rebuilds each line from the live
// product price. priceUpdated is true when any line's live price differs from the
// snapshot it was added at, so the UI can note that prices changed before charging.
export interface CheckoutCart extends CartView {
  priceUpdated: boolean;
}

// loadCheckoutCart loads the cart and re-prices every line from the live product,
// so the reviewed total matches what placeOrder charges (no silent price change —
// GDPR/consumer-trust: the customer approves the exact amount). A line whose
// product can't be read keeps its snapshot price; real placement surfaces any
// genuine problem (gone/out of stock). The authenticated server cart carries
// snapshot prices, which is where drift can appear; the anonymous device cart is
// already hydrated live, so it simply reconfirms.
export async function loadCheckoutCart(): Promise<CheckoutCart> {
  const base = await loadCart();
  let priceUpdated = false;
  let subtotal = 0;
  let currency = base.currency;
  const lines: CartViewLine[] = [];
  for (const l of base.lines) {
    try {
      const p = await getShopProduct(l.slug);
      if (p.priceCents !== l.priceCents) priceUpdated = true;
      if (!currency) currency = p.currency;
      lines.push({ ...l, priceCents: p.priceCents, currency: p.currency || l.currency });
      subtotal += p.priceCents * l.quantity;
    } catch {
      // Keep the snapshot line; placement surfaces a real problem.
      lines.push(l);
      subtotal += l.priceCents * l.quantity;
    }
  }
  return { lines, subtotalCents: subtotal, currency, priceUpdated };
}

// SET semantics per slug, mirroring the server's applyLine (the line is set to
// this quantity, not incremented); a quantity of 0 removes it.
function setDeviceLine(slug: string, quantity: number): DeviceLine[] {
  const lines = readDeviceCart();
  const qty = clampQty(quantity);
  const idx = lines.findIndex((l) => l.slug === slug);
  if (qty === 0) {
    if (idx >= 0) lines.splice(idx, 1);
  } else if (idx >= 0) {
    lines[idx] = { slug, quantity: qty };
  } else {
    lines.push({ slug, quantity: qty });
  }
  writeDeviceCart(lines);
  return lines;
}

function sumCartLines(lines: ReadonlyArray<{ quantity?: number }> | undefined): number {
  return (lines ?? []).reduce((n, l) => n + (l.quantity ?? 0), 0);
}

// CART_CHANGED_EVENT fires on the window after every cart mutation so the UI
// (the nav cart badge, an open drawer) can refresh its count + contents.
export const CART_CHANGED_EVENT = 'cart:changed';

function emitCartChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
  }
}

// addToCart puts `quantity` of `slug` in the cart and returns the new total item
// count. Signed in → the server cart (`POST /api/shop/cart`); anonymous → the
// localStorage device cart. SET semantics (matches the server): the slug's line
// is set to `quantity`, and 0 removes it.
export async function addToCart(slug: string, quantity: number): Promise<number> {
  let count: number;
  if (isAuthenticated()) {
    const cart = await updateShopCart({ slug, quantity });
    count = sumCartLines(cart.lines);
  } else {
    count = setDeviceLine(slug, quantity).reduce((n, l) => n + l.quantity, 0);
  }
  emitCartChanged();
  return count;
}

// mergeDeviceCartOnSignIn folds any anonymous device cart into the signed-in
// customer's server cart, then clears it. Call it right after authentication is
// established (boot with a refresh cookie, OAuth return, or magic-link verify) —
// it no-ops when signed out or when there's nothing to merge, so it is safe to
// call on every authInit. Union by slug keeping the LARGER quantity, so a
// returning customer never loses items already in their server cart.
// Best-effort: a line whose product is gone (404) is dropped; a transient
// failure leaves the device cart intact to retry on the next sign-in.
export async function mergeDeviceCartOnSignIn(): Promise<void> {
  if (!isAuthenticated()) return;
  const device = readDeviceCart();
  if (device.length === 0) return;

  const server = await getShopCart().catch(() => null);
  if (!server) return; // transient — keep the device cart and retry next sign-in

  const existing = new Map<string, number>();
  for (const l of server.lines ?? []) {
    if (l.slug) existing.set(l.slug, l.quantity ?? 0);
  }

  let keepForRetry = false;
  for (const line of device) {
    const have = existing.get(line.slug) ?? 0;
    const want = clampQty(Math.max(have, line.quantity));
    if (want <= have) continue; // server already holds at least as many
    try {
      await updateShopCart({ slug: line.slug, quantity: want });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) continue; // product gone — drop it
      keepForRetry = true; // transient/auth — don't lose the cart
    }
  }
  if (!keepForRetry) clearDeviceCart();
  emitCartChanged();
}
